<script lang="ts">
import { stripSiteDomain } from '$lib/utils/url.js';

interface Props {
    eyebrow?: string;
    title?: string;
    metaOne?: string;
    metaTwo?: string;
    image?: { src?: string; alt?: string };
    link?: string;
    hideBlock?: boolean;
}

let {
    eyebrow = 'Case study',
    title = '',
    metaOne = '',
    metaTwo = '',
    image = {},
    link = '',
    hideBlock = false,
}: Props = $props();

let href = $derived(stripSiteDomain(link));
</script>

{#if !hideBlock}
    {#if href}
        <a class="service-related-work-card" {href}>
            {#if image?.src}
                <img
                    class="service-related-work-card__image"
                    src={image.src}
                    alt={image.alt || title}
                />
            {/if}
            <span class="service-related-work-card__body">
                <span class="service-related-work-card__eyebrow">{eyebrow}</span>
                <span class="service-related-work-card__title">{title}</span>
                {#if metaOne || metaTwo}
                    <span class="service-related-work-card__meta">
                        {#if metaOne}<span>{metaOne}</span>{/if}
                        {#if metaOne && metaTwo}<span aria-hidden="true">•</span>{/if}
                        {#if metaTwo}<span>{metaTwo}</span>{/if}
                    </span>
                {/if}
            </span>
            <span class="service-related-work-card__arrow" aria-hidden="true"></span>
            <svg class="service-related-work-card__border" aria-hidden="true" focusable="false">
                <rect pathLength="1"></rect>
            </svg>
        </a>
    {:else}
        <div class="service-related-work-card">
            {#if image?.src}
                <img
                    class="service-related-work-card__image"
                    src={image.src}
                    alt={image.alt || title}
                />
            {/if}
            <span class="service-related-work-card__body">
                <span class="service-related-work-card__eyebrow">{eyebrow}</span>
                <span class="service-related-work-card__title">{title}</span>
                {#if metaOne || metaTwo}
                    <span class="service-related-work-card__meta">
                        {#if metaOne}<span>{metaOne}</span>{/if}
                        {#if metaOne && metaTwo}<span aria-hidden="true">•</span>{/if}
                        {#if metaTwo}<span>{metaTwo}</span>{/if}
                    </span>
                {/if}
            </span>
            <span class="service-related-work-card__arrow" aria-hidden="true"></span>
        </div>
    {/if}
{/if}

<style lang="scss">
    .service-related-work-card {
        align-items: stretch;
        background: var(--colour-classic-chalk);
        border-radius: 0.8rem;
        color: var(--colour-dreamless-sleep);
        display: flex;
        min-height: 9.3rem;
        overflow: hidden;
        position: relative;
        text-decoration: none;
    }

    a.service-related-work-card:focus-visible {
        outline: none;
    }

    .service-related-work-card__image {
        aspect-ratio: 1;
        flex: 0 0 9.3rem;
        object-fit: cover;
        width: 9.3rem;
    }

    .service-related-work-card__body {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
        padding: 1.6rem;
    }

    .service-related-work-card__eyebrow {
        color: var(--colour-grey);
        font-size: 1rem;
        line-height: 1.2;
        text-transform: uppercase;
    }

    .service-related-work-card__title {
        font-size: 1.4rem;
        line-height: 1.2;
        margin-top: 0.4rem;
    }

    .service-related-work-card__meta {
        align-items: center;
        color: var(--colour-grey);
        display: flex;
        flex-wrap: wrap;
        font-size: 1.2rem;
        gap: 0.8rem;
        line-height: 1.5;
        margin-top: 0.4rem;
    }

    .service-related-work-card__arrow {
        align-items: center;
        display: flex;
        flex: 0 0 4.4rem;
        justify-content: center;

        &::before {
            background: url('/src/stories/assets/arrow-circle-right.svg') no-repeat center;
            background-size: 100% 100%;
            content: '';
            display: block;
            height: 1.9894rem;
            width: 2.0814rem;
        }
    }

    .service-related-work-card__border {
        display: block;
        height: 100%;
        inset: 0;
        pointer-events: none;
        position: absolute;
        width: 100%;
        z-index: 2;
    }

    .service-related-work-card__border rect {
        fill: none;
        height: calc(100% - 1px);
        rx: 0.8rem;
        ry: 0.8rem;
        opacity: 0;
        stroke: var(--colour-noghrei-silver);
        stroke-dasharray: 1;
        stroke-dashoffset: 1;
        stroke-width: 1px;
        transition:
            opacity 0.22s ease-out,
            stroke-dashoffset 0s linear 0.22s;
        vector-effect: non-scaling-stroke;
        width: calc(100% - 1px);
        x: 0.5px;
        y: 0.5px;
    }

    a.service-related-work-card:hover .service-related-work-card__border rect,
    a.service-related-work-card:focus-visible .service-related-work-card__border rect {
        opacity: 1;
        stroke-dashoffset: 0;
        transition:
            opacity 0s,
            stroke-dashoffset 0.44s cubic-bezier(0.22, 1, 0.36, 1);
    }

    @media (prefers-reduced-motion: reduce) {
        .service-related-work-card__border rect {
            transition-duration: 0s;
        }
    }
</style>
